import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { faArrowLeft, faPrint } from '@fortawesome/free-solid-svg-icons';
import useConsultas from '../hooks/useConsultas';
import Masks from '../shared/masks';
import Button from '../components/Button';
import { useAuth } from '../hooks/useAuth';

export default function ConsultasPrintById() {
  const { currentOrganization } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { consulta, getById } = useConsultas();
  const { NumberToDecimalMask } = Masks();

  useEffect(() => {
    if (id) {
      getById(id);
    }
  }, [id, getById]);

  const handleGoBack = (): void => {
    navigate(-1);
  };

  const handlePrint = (): void => {
    window.print();
  };

  if (!consulta || !consulta.paciente) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando consulta...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="m-20 flex flex-col items-center">
      <div className="print:hidden mb-8 flex gap-4">
        <Button type="button" onClick={handleGoBack} variant="outline" leftIcon={faArrowLeft}>
          Voltar
        </Button>
        <Button type="button" onClick={handlePrint} variant="primary" leftIcon={faPrint}>
          Imprimir
        </Button>
      </div>
      <div className="bg-white rounded-lg border shadow-sm w-[800px] print:w-full print:border-none print:shadow-none p-8 mb-4 break-after-page">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {[currentOrganization?.name, currentOrganization?.slogan].filter(Boolean).join(' | ')}
          </h1>
          <h2 className="text-lg font-semibold text-gray-700 bg-gray-50 py-2 px-4 rounded-lg">
            Prescrição
          </h2>
        </div>
        <div className="space-y-4 mb-8">
          <div className="flex items-center">
            <span className="font-semibold text-gray-700 w-20">Paciente:</span>
            <span className="text-gray-900">{consulta.paciente?.nome}</span>
          </div>
          <div className="flex items-center">
            <span className="font-semibold text-gray-700 w-20">Data:</span>
            <span className="text-gray-900">
              {consulta.created_at ? new Date(consulta.created_at).toLocaleDateString('pt-BR') : ''}
            </span>
          </div>
        </div>
        <div className="bg-gray-50 p-6 rounded-lg min-h-32">
          <p className="text-gray-900 whitespace-pre-line">
            {consulta.conduta || 'Nenhuma conduta registrada'}
          </p>
        </div>
      </div>
      <div className="bg-white rounded-lg border shadow-sm w-[800px] print:w-full print:border-none print:shadow-none p-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {[currentOrganization?.name, currentOrganization?.slogan].filter(Boolean).join(' | ')}
          </h1>
          <h2 className="text-lg font-semibold text-gray-700 bg-gray-50 py-2 px-4 rounded-lg">
            Prescrição de Óculos
          </h2>
        </div>
        <div className="space-y-4 mb-8">
          <div className="flex items-center">
            <span className="font-semibold text-gray-700 w-20">Paciente:</span>
            <span className="text-gray-900">{consulta.paciente?.nome}</span>
          </div>
          <div className="flex items-center">
            <span className="font-semibold text-gray-700 w-20">Data:</span>
            <span className="text-gray-900">
              {consulta.created_at ? new Date(consulta.created_at).toLocaleDateString('pt-BR') : ''}
            </span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse bg-white">
            <thead>
              <tr className="bg-gray-50">
                <th className="border border-gray-300 p-3 text-left font-semibold text-gray-700"></th>
                <th className="border border-gray-300 p-3 text-center font-semibold text-gray-700">
                  Esférico
                </th>
                <th className="border border-gray-300 p-3 text-center font-semibold text-gray-700">
                  Cilíndrico
                </th>
                <th className="border border-gray-300 p-3 text-center font-semibold text-gray-700">
                  Eixo
                </th>
                <th className="border border-gray-300 p-3 text-center font-semibold text-gray-700">
                  AV
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="hover:bg-gray-50">
                <td className="border border-gray-300 p-3 font-semibold text-gray-700">OD</td>
                <td className="border border-gray-300 p-3 text-center font-mono">
                  {consulta.od_esferico
                    ? consulta.od_esferico > 0
                      ? `+${NumberToDecimalMask(consulta.od_esferico)}`
                      : NumberToDecimalMask(consulta.od_esferico)
                    : '-'}
                </td>
                <td className="border border-gray-300 p-3 text-center font-mono">
                  {consulta.od_cilindrico
                    ? consulta.od_cilindrico > 0
                      ? `+${NumberToDecimalMask(consulta.od_cilindrico)}`
                      : NumberToDecimalMask(consulta.od_cilindrico)
                    : '-'}
                </td>
                <td className="border border-gray-300 p-3 text-center font-mono">
                  {consulta.od_eixo || '-'}
                </td>
                <td className="border border-gray-300 p-3 text-center font-mono">
                  {consulta.od_av || '-'}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="border border-gray-300 p-3 font-semibold text-gray-700">OE</td>
                <td className="border border-gray-300 p-3 text-center font-mono">
                  {consulta.oe_esferico
                    ? consulta.oe_esferico > 0
                      ? `+${NumberToDecimalMask(consulta.oe_esferico)}`
                      : NumberToDecimalMask(consulta.oe_esferico)
                    : '-'}
                </td>
                <td className="border border-gray-300 p-3 text-center font-mono">
                  {consulta.oe_cilindrico
                    ? consulta.oe_cilindrico > 0
                      ? `+${NumberToDecimalMask(consulta.oe_cilindrico)}`
                      : NumberToDecimalMask(consulta.oe_cilindrico)
                    : '-'}
                </td>
                <td className="border border-gray-300 p-3 text-center font-mono">
                  {consulta.oe_eixo || '-'}
                </td>
                <td className="border border-gray-300 p-3 text-center font-mono">
                  {consulta.oe_av || '-'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        {consulta.adicao && (
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center">
              <span className="font-semibold text-blue-800 mr-3">Adição:</span>
              <span className="font-mono text-blue-900">
                {consulta.adicao > 0
                  ? `+${NumberToDecimalMask(consulta.adicao)}`
                  : NumberToDecimalMask(consulta.adicao)}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
